import { useEffect } from "react";
import { animatesOn, looksLikeCard, staggerDelay } from "../cardFxMath";

const SKIP_INSIDE = "[role='dialog'], .fx-card, form, header, nav, footer, [data-no-fx]";

// Gives every card on the public pages the same life: cards in a grid rise into place one after another as
// they scroll into view, and lift with a soft cyan spotlight that follows the pointer when hovered.
// One observer for the whole site, so no page needs its own code. It does nothing for people who ask their
// device for reduced motion, and if anything is missing from the browser the cards simply stay as they are.
export default function CardFX() {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined" || typeof MutationObserver === "undefined") return undefined;
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const seen = new WeakSet();
    const reveal = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        reveal.unobserve(el);
        el.classList.remove("fx-await");
        el.classList.add("fx-reveal");
        el.addEventListener("animationend", () => el.classList.remove("fx-reveal"), { once: true });
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });

    const onMove = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      e.currentTarget.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      e.currentTarget.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };

    const scan = () => {
      if (!animatesOn(window.location.pathname)) return;
      document.querySelectorAll(".grid > *, [class*='grid-cols'] > *, article").forEach((el) => {
        if (seen.has(el) || el.closest(SKIP_INSIDE)) return;
        const rect = el.getBoundingClientRect();
        if (!looksLikeCard(el.getAttribute("class"), { width: rect.width, height: rect.height, tag: el.tagName })) return;
        seen.add(el);
        const index = [...el.parentElement.children].indexOf(el);
        el.style.setProperty("--fx-delay", `${staggerDelay(index)}ms`);
        el.classList.add("fx-hover", "fx-await");
        el.addEventListener("pointermove", onMove);
        reveal.observe(el);
      });
    };

    let queued = 0;
    const queue = () => {
      if (queued) return;
      queued = window.setTimeout(() => { queued = 0; scan(); }, 120);
    };
    const mutations = new MutationObserver(queue);
    mutations.observe(document.body, { childList: true, subtree: true });
    queue();

    return () => {
      window.clearTimeout(queued);
      mutations.disconnect();
      reveal.disconnect();
      document.querySelectorAll(".fx-await").forEach((el) => el.classList.remove("fx-await"));   // never leave a card hidden
    };
  }, []);

  return null;
}
